import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Lock, Shield, KeyRound, CheckCircle2, QrCode, Loader2 } from 'lucide-react';

export const SecurityPage: React.FC = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordSuccess, setPasswordSuccess] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState<boolean>(false);

  // 2FA state
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(user?.mfaEnabled ?? false);
  const [totpCode, setTotpCode] = useState<string>('');
  const [mfaSuccess, setMfaSuccess] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaBusy, setMfaBusy] = useState<boolean>(false);
  const [enroll, setEnroll] = useState<{ secret: string; otpauthUrl: string; recoveryCodes: string[] } | null>(null);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    setSavingPassword(true);
    try {
      // Real change: verifies the current password and persists the new one in
      // auth-service (POST /v2/auth/change-password).
      await api.changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError((err as { message?: string })?.message || 'Could not change password — check your current password.');
    } finally {
      setSavingPassword(false);
    }
  };

  const flashMfa = (msg: string) => { setMfaSuccess(msg); setTimeout(() => setMfaSuccess(null), 4000); };

  // Start enrollment: fetch the secret + recovery codes to show for setup.
  const handleStartEnroll = async () => {
    setMfaError(null);
    setMfaBusy(true);
    try {
      setEnroll(await api.enableMfa());
      setTotpCode('');
    } catch (err) {
      setMfaError((err as { message?: string })?.message || 'Could not start 2FA setup.');
    } finally {
      setMfaBusy(false);
    }
  };

  // Confirm the code → activate 2FA.
  const handleVerifyEnroll = async () => {
    setMfaError(null);
    setMfaBusy(true);
    try {
      await api.verifyMfa(totpCode.trim());
      setMfaEnabled(true);
      setEnroll(null);
      setTotpCode('');
      flashMfa('Two-factor authentication is now on.');
    } catch (err) {
      setMfaError((err as { message?: string })?.message || 'That code was not valid. Try the current one.');
    } finally {
      setMfaBusy(false);
    }
  };

  // Disable, confirmed with a current code.
  const handleDisableMfa = async () => {
    const code = window.prompt('Enter a current 6-digit code from your authenticator to turn 2FA off:');
    if (code === null) return;
    setMfaError(null);
    setMfaBusy(true);
    try {
      await api.disableMfa(code.trim());
      setMfaEnabled(false);
      flashMfa('Two-factor authentication is off.');
    } catch (err) {
      setMfaError((err as { message?: string })?.message || 'Could not disable 2FA — check the code.');
    } finally {
      setMfaBusy(false);
    }
  };

  return (
    <div>
      <div
        style={{
          background: 'var(--bg-surface-card)',
          border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)',
          padding: '16px 20px',
          marginBottom: '20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
          <Lock size={18} color="var(--primary-light)" />
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>
            Admin Account & Security
          </h2>
        </div>
        <p style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
          Manage login credentials, TOTP two-factor enrollment, and session security.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '20px' }}>
        {/* Profile & 2FA Configuration */}
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '14px' }}>
            Two-Factor Authentication (TOTP / MFA)
          </h3>

          <div style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '14px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>MFA Status</span>
              <StatusBadge status={mfaEnabled ? 'active' : 'warning'} />
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              MFA challenge is required at login. Operators must verify with an authenticator app (Google Authenticator, 1Password).
            </p>
          </div>

          {mfaSuccess && (
            <div
              style={{
                background: 'var(--success-bg)',
                border: '1px solid var(--success-border)',
                color: 'var(--success-text)',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                marginBottom: '16px',
                fontSize: '12.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <CheckCircle2 size={16} />
              <span>{mfaSuccess}</span>
            </div>
          )}

          {mfaError && (
            <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '12.5px' }}>{mfaError}</div>
          )}

          {mfaEnabled ? (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--success-text)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Shield size={16} />
                <span>This account is protected with an authenticator app (TOTP).</span>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={handleDisableMfa} disabled={mfaBusy}>
                {mfaBusy ? <Loader2 size={14} className="spin" /> : null}
                <span>Turn off 2FA</span>
              </button>
            </div>
          ) : enroll ? (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                Add this account to your authenticator app (Google Authenticator, 1Password, Authy) — enter the key below manually, then type the 6-digit code it shows.
              </div>
              <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginBottom: '4px' }}>Setup key</div>
                <code style={{ color: 'var(--primary-light)', fontSize: '14px', letterSpacing: '1px', wordBreak: 'break-all' }}>{enroll.secret}</code>
              </div>
              {enroll.recoveryCodes.length > 0 && (
                <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '11.5px', color: '#f59e0b', marginBottom: '6px', fontWeight: 600 }}>Save these recovery codes somewhere safe — each works once if you lose your device:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-main)' }}>
                    {enroll.recoveryCodes.map((c) => <span key={c}>{c}</span>)}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="text" inputMode="numeric" className="input" placeholder="Enter 6-digit code" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))} maxLength={6} />
                <button className="btn btn-primary btn-sm" onClick={handleVerifyEnroll} disabled={mfaBusy || totpCode.length < 6}>
                  {mfaBusy ? <Loader2 size={14} className="spin" /> : <Shield size={14} />}
                  <span>Verify &amp; turn on</span>
                </button>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => { setEnroll(null); setMfaError(null); }} style={{ marginTop: '8px' }}>Cancel</button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                Protect this account with an authenticator-app code at login. Strongly recommended for staff.
              </div>
              <button className="btn btn-primary btn-sm" onClick={handleStartEnroll} disabled={mfaBusy}>
                {mfaBusy ? <Loader2 size={14} className="spin" /> : <QrCode size={14} />}
                <span>Set up 2FA</span>
              </button>
            </div>
          )}
        </div>

        {/* Change Password Form */}
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '14px' }}>
            Change Password
          </h3>

          {passwordSuccess && (
            <div
              style={{
                background: 'var(--success-bg)',
                border: '1px solid var(--success-border)',
                color: 'var(--success-text)',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                marginBottom: '16px',
                fontSize: '12.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <CheckCircle2 size={16} />
              <span>Password updated successfully.</span>
            </div>
          )}

          {passwordError && (
            <div
              style={{
                background: 'rgba(239,68,68,0.10)',
                border: '1px solid rgba(239,68,68,0.35)',
                color: 'var(--danger-text)',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                marginBottom: '16px',
                fontSize: '12.5px',
              }}
            >
              {passwordError}
            </div>
          )}

          <form onSubmit={handlePasswordChange}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Current Password
              </label>
              <input
                type="password"
                className="input"
                style={{ width: '100%' }}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                New Password
              </label>
              <input
                type="password"
                className="input"
                style={{ width: '100%' }}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Confirm New Password
              </label>
              <input
                type="password"
                className="input"
                style={{ width: '100%' }}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={savingPassword}>
                {savingPassword ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />}
                <span>{savingPassword ? 'Updating…' : 'Update Password'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
